import React, { useRef, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import OGEAvatar from '@/components/OGE/OGEAvatar'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { sendTestChatMessage } from '@/redux/asyncThunks/testChatWebsocket'
import { sendAIPlayerTestChatMessage } from '@/redux/asyncThunks/aiPlayerTestChatWebsocket'
import Typing from '@/components/Room/Typing'
import { Message, RAGChunk } from '@/redux/slices/testChatSlice'
import { cn } from '@/lib/utils'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown, ChevronUp, FileText } from 'lucide-react'
import { TestChatType } from '@/constants/chat'
import LlmModelBadge from '@/components/OGE/LlmModelBadge'
import { formatTime } from '@/utils/dateTimeUtils'

interface TestChatMessagesProps {
  className?: string
  messages: Message[]
  readOnly?: boolean
  chatType?: TestChatType
}

const TestChatMessages: React.FC<TestChatMessagesProps> = React.memo(
  ({ className = '', messages, readOnly = false, chatType = TestChatType.Advisor }) => {
    const { isGenerating, error, isConnected } = useAppSelector((state) =>
      chatType === TestChatType.Advisor
        ? state.testChatMessages
        : state.aiPlayerTestChatMessages
    )
    const user = useSelector((state: RootState) => state.user.user)
    const dispatch = useAppDispatch()
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const [textInput, setTextInput] = useState('')
    const [expandedChunks, setExpandedChunks] = useState<Set<number | string>>(
      new Set()
    )

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)

      return () => clearTimeout(timeoutId)
    }, [messages])

    const isStreaming = messages.some((message) => message.streaming)
    const isBotStreaming = messages.some(
      (message) => message.streaming && message.user !== user?.email
    )

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTextInput(e.target.value)
    }

    const handleInputKeyDown = (
      e: React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (textInput.trim()) {
          if (chatType === TestChatType.Advisor) {
            dispatch(sendTestChatMessage({ message: textInput, user: user?.email }))
          } else {
            dispatch(
              sendAIPlayerTestChatMessage({ message: textInput, user: user?.email })
            )
          }
          setTextInput('')
        }
      }
    }

    const handleSendMessage = (e: React.FormEvent) => {
      e.preventDefault()
      if (textInput.trim()) {
        if (chatType === TestChatType.Advisor) {
          dispatch(sendTestChatMessage({ message: textInput }))
        } else {
          dispatch(sendAIPlayerTestChatMessage({ message: textInput }))
        }
        setTextInput('')
      }
    }

    const toggleChunks = (messageId: number | string) => {
      setExpandedChunks((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(messageId)) {
          newSet.delete(messageId)
        } else {
          newSet.add(messageId)
        }
        return newSet
      })
    }

    const renderRAGChunks = (
      chunks: RAGChunk[],
      messageId: number | string
    ) => {
      if (!chunks || chunks.length === 0) return null

      const isExpanded = expandedChunks.has(messageId)

      return (
        <Collapsible
          open={isExpanded}
          onOpenChange={() => toggleChunks(messageId)}
        >
          <CollapsibleTrigger asChild>
            <button
              className={cn(
                'flex justify-between items-center px-3 py-2 mt-2 w-full text-xs font-medium',
                'text-gray-600 rounded-lg transition-colors hover:text-gray-800 hover:bg-gray-100',
                'border border-gray-200'
              )}
            >
              <div className='flex gap-2 items-center'>
                <FileText className='w-3 h-3' />
                <span>
                  {chunks.length} retrieved chunk
                  {chunks.length !== 1 ? 's' : ''}
                </span>
              </div>
              {isExpanded ? (
                <ChevronUp className='w-3 h-3' />
              ) : (
                <ChevronDown className='w-3 h-3' />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className='mt-2 space-y-2'>
            {chunks.map((chunk, chunkIndex) => (
              <div
                key={chunkIndex}
                className='p-3 text-xs bg-gray-50 rounded-lg border border-gray-200'
              >
                <div className='flex justify-between items-start mb-2'>
                  <div className='flex-1 min-w-0'>
                    <div className='mb-1 font-semibold text-gray-700 break-words overflow-wrap-anywhere'>
                      {chunk.filename || 'Unknown file'}
                    </div>
                    <div className='text-gray-500 text-[10px]'>
                      Score: {(chunk.score * 100).toFixed(1)}% | Chunk #
                      {chunk.chunkIndex + 1}
                    </div>
                  </div>
                </div>
                <div className='mt-2 leading-relaxed text-gray-700 whitespace-pre-wrap break-words'>
                  {chunk.text}
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )
    }

    return (
      <div className={cn('flex flex-col h-full', className)}>
        <div className='overflow-auto flex-1 p-4'>
          {error && (
            <div className='p-4 mb-4 bg-red-50 rounded-lg border border-red-200'>
              <p className='text-sm text-red-600'>{error}</p>
            </div>
          )}

          {!isConnected && !readOnly && (
            <div className='p-4 mb-4 bg-yellow-50 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-600'>Connecting to chat...</p>
            </div>
          )}

          {messages.length === 0 ? (
            <div className='flex justify-center items-center w-full h-full'>
              <div className='text-center'>
                <p className='mb-2 text-xl text-gray-600'>No messages yet</p>
                <p className='text-gray-600'>Start a conversation</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, index) => {
                const isCurrentUser = message.user === user?.email

                return (
                  <div
                    key={`${message.id}-${index}`}
                    className={cn(
                      'flex mb-4',
                      isCurrentUser ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={cn(
                        'max-w-[80%] p-4 rounded-[20px] bg-[#E6ECF1] break-words wrap-anywhere break-normal',
                        isCurrentUser ? 'text-right' : 'text-left'
                      )}
                    >
                      {!isCurrentUser && (
                        <div className='flex gap-2 items-center mb-1'>
                          <OGEAvatar
                            src='/images/avatar-image.svg'
                            fallback={'AB'}
                            variant='sm'
                            isLoading={false}
                          />
                          <span className='text-base font-medium text-dark-blue'>
                            {message.user}
                          </span>
                        </div>
                      )}

                      <div
                        className={cn(
                          'text-sm font-medium text-black break-words',
                          message.streaming && !isCurrentUser
                            ? 'animate-pulse'
                            : ''
                        )}
                      >
                        <div className='chat-markdown max-w-none break-words prose prose-sm wrap-anywhere'>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {message.message}
                          </ReactMarkdown>
                        </div>
                        {message.streaming && !isCurrentUser && (
                          <span className='ml-1 animate-pulse'>▋</span>
                        )}
                      </div>
                      {!isCurrentUser && message.ragChunks && (
                        <div className='mt-2'>
                          {renderRAGChunks(message.ragChunks, message.id)}
                        </div>
                      )}

                      {!isCurrentUser &&
                        (() => {
                          const hasModelInfo =
                            !!message.llm_model_name || !!message.llm_provider
                          const showFooter =
                            !!message.created_at || hasModelInfo
                          if (!showFooter) return null
                          return (
                            <div className='flex justify-end items-center gap-2 mt-2'>
                              {hasModelInfo && (
                                <LlmModelBadge
                                  llmModelName={message.llm_model_name}
                                  llmProvider={message.llm_provider}
                                />
                              )}
                              {message.created_at && (
                                <span className='text-xs text-gray-500 opacity-75'>
                                  {formatTime(message.created_at)}
                                </span>
                              )}
                            </div>
                          )
                        })()}
                    </div>
                  </div>
                )
              })}

              {(() => {
                const lastMsg = messages[messages.length - 1]
                const isUserLast =
                  lastMsg && lastMsg.user === user?.email && isGenerating

                if (isUserLast && !isBotStreaming) {
                  return (
                    <div className='flex justify-start mb-4'>
                      <Typing />
                    </div>
                  )
                }
                return null
              })()}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {!readOnly && (
          <div className='bg-white border-t border-gray-100'>
            <form onSubmit={handleSendMessage} className='p-3'>
              <div className='relative min-h-[50px] flex items-start flex-1 px-3 py-2 border border-[#CBD5E1] bg-[#E6ECF2] rounded-2xl'>
                <div className='flex flex-wrap gap-1 items-center w-full'>
                  <Textarea
                    ref={inputRef}
                    value={textInput}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    placeholder='Type your message...'
                    rows={1}
                    className='flex-1 min-w-[100px] min-h-[36px] max-h-40 resize-none overflow-y-auto text-sm leading-[36px] bg-transparent border-none shadow-none focus:outline-none focus:ring-0 focus:border-none focus:shadow-none focus-visible:outline-none focus-visible:ring-0 appearance-none'
                    disabled={isStreaming || !isConnected}
                  />
                </div>

                <div className='flex items-center ml-2'>
                  <Button
                    className='p-1 bg-transparent shadow-none hover:bg-transparent'
                    disabled={!textInput.trim() || isStreaming || !isConnected}
                    type='submit'
                    size='icon'
                  >
                    <img
                      src={
                        !textInput.trim() || isStreaming || !isConnected
                          ? '/icons/arrow-up-circle-white.svg'
                          : '/icons/arrow-up-circle.svg'
                      }
                      alt='Send'
                      className='w-7 h-7'
                    />
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    )
  }
)

TestChatMessages.displayName = 'TestChatMessages'

export default TestChatMessages
